import React from 'react'
import clsx from 'clsx'
import { ArrowLeft } from 'iconsax-react'
import { AddErrorAlert, AddSuccessAlert } from '../../../../../slices/alert'
import { useAppDispatch } from '../../../../../hooks/redux'

import BaseModal from '../../../../molecules/ChatModals/BaseModal2'
import Button from '../../../../atoms/Button2'
import ButtonSecondary from '../../../../atoms/ButtonSecondary'
import InboxSelectMenu from '../../../../molecules/InboxSelectMenu'
import MediaAvatar from '../../../../atoms/MediaAvatar'
import useDelayedUserSearch, {
  EXCLUDE_ME
} from '../../../../../hooks/useDelayedUserSearch'
import RemoveAgentIcon from './Icon'
import TransferSuccessMessage from '../../../../../pages/ClientChats/TransferSuccessMessage'

import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import { useUserAPI } from '../../../../../providers/UserAPIProvider'
import { formatSessionForPut } from '../../../../../utilities'
import { updateSession } from '../../../../../lib/api'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'
import { useQueueState } from '../../../../../providers/QueueStateProvider'
import { deleteConversation } from '../../../../../slices/convo'

import useStyles from './styles'

const TransferConversationModal = ({
  owner,
  conversation,
  onClose,
  allUsers
}) => {
  const dispatch = useAppDispatch()
  const { isSmall, isMedium } = useBreakpoint()
  const { user: currentUser } = useEnvironment()
  const { search, results } = useDelayedUserSearch(
    false,
    EXCLUDE_ME(() => currentUser?.id)
  )
  const { handleSelect: handleConvoSelect } = useQueueState()
  const { updateUsers } = useUserAPI()
  const [inbox, setInbox] = React.useState(null)
  const [agent, setAgent] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const fullOwner = allUsers[owner]
  const classes = useStyles()

  const current = React.useMemo(
    () => formatSessionForPut(conversation),
    [conversation]
  )

  React.useEffect(() => {
    updateUsers(results)
  }, [updateUsers, results])

  const onSearchChange = React.useCallback(
    value => {
      search(value)
    },
    [search]
  )

  const handleTransferClick = React.useCallback(async () => {
    const newSession = {
      ...current,
      inbox_id: inbox.id,
      assigned_to: agent?.id || current?.assigned_to
    }
    const { id, ...rest } = newSession

    delete rest.userIds
    setLoading(true)

    try {
      // @ts-ignore
      await updateSession(id, rest)
      if (newSession.inbox_id !== current.inbox_id) {
        dispatch(deleteConversation([id]))
      }
      handleConvoSelect('')
      dispatch(
        AddSuccessAlert(<TransferSuccessMessage inbox={inbox} agent={agent} />)
      )
      onClose()
    } catch (err) {
      console.error('Error moving conversation', err)
      dispatch(AddErrorAlert({ err }))
    } finally {
      setLoading(false)
    }
  }, [current, inbox, agent, handleConvoSelect, onClose, dispatch])

  return (
    <React.Fragment>
      <BaseModal
        open
        //@ts-ignore
        title={
          isMedium ? (
            <>
              <ArrowLeft className={classes.returnIcon} onClick={onClose} />
              Transfer
            </>
          ) : (
            'Transfer'
          )
        }
        onClose={onClose}
        showTitleOnMobile
        hideTitle={false}
        showX={!isMedium}
        PaperProps2={{
          style: {
            padding: '30px 16px 30px 16px',
            border: '1px solid #C6C6C6',
            borderRadius: '4px',
            width: '100%',
            maxWidth: isMedium ? 'unset' : '35%',
            height: isSmall && '100svh',
            maxHeight: isSmall && 'unset',
            minHeight: isSmall && 'auto'
          }
        }}
        classes={{ title: classes.modalTitle, close: classes.modalClose }}
        style={{ marginBottom: isMedium && 66 }}
      >
        <div className={classes.root}>
          <div className={classes.groupContainer}>
            <label className={classes.transferName}>
              Transfer '<strong>{fullOwner?.name}</strong>' to:
            </label>
          </div>
          <div className={classes.groupContainer}>
            <label className={classes.inboxLabel}>Select Inbox</label>
            <div className={classes.inboxSelect}>
              <InboxSelectMenu onSelect={setInbox} />
            </div>
          </div>

          <div className={classes.groupContainer}>
            <label className={classes.inboxLabel}>
              Select Agent (optional)
            </label>
            <div className={classes.agentContainer}>
              {agent ? (
                <div className={classes.chip}>
                  <MediaAvatar className={classes.chipAvatar} user={agent} />
                  <label>{agent?.name}</label>
                  <RemoveAgentIcon
                    onClick={() => {
                      onSearchChange('')
                      setAgent(null)
                    }}
                    className={classes.chipAction}
                  />
                </div>
              ) : (
                <>
                  <input
                    className={classes.agentSearchInput}
                    placeholder='Search agent and teammates'
                    onChange={e => {
                      onSearchChange(e.target.value)
                    }}
                  />
                </>
              )}
            </div>
            {!agent && results.length > 0 && (
              <div className={classes.resultsContainer}>
                <label className={classes.ReAssignLabel}>Re-Assign</label>
                <ul className={classes.searchResultContainer}>
                  {results.map(res => (
                    <li
                      key={res.id}
                      className={classes.searchResult}
                      onClick={() => {
                        setAgent(res)
                        onSearchChange('')
                      }}
                    >
                      <MediaAvatar className={classes.avatar} user={res} />
                      {res?.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className={classes.spacer}></div>
        <div className={classes.buttonSection}>
          <ButtonSecondary className={classes.button} onClick={() => onClose()}>
            Cancel
          </ButtonSecondary>
          <Button
            onClick={handleTransferClick}
            className={clsx(classes.button, classes.transferBtn)}
            disabled={!Boolean(inbox) || loading}
          >
            Transfer
          </Button>
        </div>
      </BaseModal>
    </React.Fragment>
  )
}

export default TransferConversationModal
